import { type IntlShape, useIntl } from 'react-intl';
import { VariableSelector } from 'sonata-platform-components';
import { Checkbox, RadioButtonsGroup } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { normalizeIABVariables } from 'sonata-static-storage/lib/helpers/variables.helpers';
import { iabCategoriesDictionary } from 'sonata-static-storage/lib/samples/iabCategories.samples';
import { iabCategoriesV2Dictionary } from 'sonata-static-storage/lib/samples/iabCategoriesV2.samples';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';

import './iabCategoriesSelector.scss';

const { levels: iabCategoriesLevels } = normalizeIABVariables(iabCategoriesDictionary);
const { levels: iabCategoriesLevelsV2 } = normalizeIABVariables(iabCategoriesV2Dictionary);

export const getVariableSelectorOptions = (
  iabLevels: { [key: string]: Record<string, any>[] },
  formatMessage: IntlShape['formatMessage']
) => {
  const options = [
    {
      id: 'level0',
      label: formatMessage({
        defaultMessage: 'Categories',
        id: 'IABCategoriesSelector.categories'
      })
    },
    {
      id: 'level1',
      label: formatMessage({
        defaultMessage: 'Subcategories',
        id: 'IABCategoriesSelector.subcategories'
      })
    },
    {
      id: 'level2',
      label: formatMessage({
        defaultMessage: 'Subcategories 2',
        id: 'IABCategoriesSelector.subcategories2'
      })
    },
    {
      id: 'level3',
      label: formatMessage({
        defaultMessage: 'Subcategories 3',
        id: 'IABCategoriesSelector.subcategories3'
      })
    },
    {
      id: 'level4',
      label: formatMessage({
        defaultMessage: 'Subcategories 4',
        id: 'IABCategoriesSelector.subcategories4'
      })
    }
  ];

  const levelsSize = Object.keys(iabLevels).length;
  return options.slice(0, levelsSize);
};

export function IABCategoriesSelector() {
  const { formatMessage } = useIntl();
  const {
    setProp,
    setUnknownIncluded,
    targeting: { contextualTargeting, unknownIncluded },
    user,
    userRoles
  } = useTargetingContext();

  const hasIABTargetingCategoriesV2Permission = checkUserResourcePermissions(
    'iabTargetingCategoriesV2',
    user,
    userRoles
  );

  const radioGroupItems = [
    {
      id: '1',
      text: formatMessage({
        defaultMessage: 'IAB v.1',
        id: 'targeting.appAndSitesCategories.radioButtonsList.item1'
      })
    },
    {
      id: '2',
      isDisabled: !hasIABTargetingCategoriesV2Permission,
      text: formatMessage({
        defaultMessage: 'IAB v.2',
        id: 'targeting.appAndSitesCategories.radioButtonsList.item2'
      })
    }
  ];

  const handleVersionChange = (optionId: string) => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      iabCategories: {
        ...contextualTargeting?.iabCategories,
        categories: [],
        version: parseInt(optionId)
      }
    });
  };

  const handleAllowAvoidChange = (isAvoid: boolean) => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      iabCategories: {
        ...contextualTargeting?.iabCategories,
        avoid: isAvoid
      }
    });
  };

  const handleCategoriesSelection = (categoryIds: string[]) => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      iabCategories: {
        ...contextualTargeting?.iabCategories,
        categories: categoryIds,
        enabled: Boolean(categoryIds.length)
      }
    });
  };

  const isAvoidance = Boolean(contextualTargeting?.iabCategories?.avoid);
  const selectedVersion = contextualTargeting?.iabCategories?.version?.toString() || '1';
  const idsSelectedItems = contextualTargeting?.iabCategories?.categories || [];
  const showUnknownIncluded =
    idsSelectedItems.length > 0 && checkUserResourcePermissions('unknownIncluded', user, userRoles);

  const iabLevels = selectedVersion === '1' ? iabCategoriesLevels : iabCategoriesLevelsV2;
  const options = getVariableSelectorOptions(iabLevels, formatMessage);

  return (
    <div className="iabSelector">
      <RadioButtonsGroup
        id="iab-category-version-radio"
        items={radioGroupItems}
        onChange={handleVersionChange}
        selectedItem={selectedVersion}
      />
      <div className="iabSelector__container">
        <div className="iabSelector__options">
          <div>
            {showUnknownIncluded && (
              <Checkbox
                classes="iabSelector__optionsSwitch"
                defaultActive={unknownIncluded.contentCategories}
                id="targetUnknown-IAB-switch"
                labelText={formatMessage({
                  defaultMessage: 'Target unknown',
                  id: 'targeting.contentCategories.targetUnknown'
                })}
                onClickAction={(_, value) => setUnknownIncluded('contentCategories', value)}
                tiny
                type="switch"
              />
            )}
          </div>
          <Checkbox
            classes="iabSelector__optionsSwitch"
            defaultActive={isAvoidance}
            id="avoid-IAB-switch"
            labelText={formatMessage({
              defaultMessage: 'Select categories to be excluded',
              id: 'targeting.contentCategories.avoidanceLabel'
            })}
            onClickAction={(_, checked) => handleAllowAvoidChange(checked)}
            tiny
            type="switch"
          />
        </div>
        <div className="iabSelector__categories">
          <VariableSelector
            boxBgColor="boxBgColor01"
            idsSelectedItems={idsSelectedItems}
            items={iabLevels}
            onChange={handleCategoriesSelection}
            options={options}
            padding="0"
            showTotalSelected={false}
          />
        </div>
      </div>
    </div>
  );
}
